import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import Select from 'react-select';
import * as XLSX from 'xlsx';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend);

// Custom icons
const positiveIcon = L.divIcon({
  className: 'custom-icon',
  html: '<div style="background-color: red; width: 20px; height: 20px; border-radius: 50%; border: 2px solid white;"></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const negativeIcon = L.divIcon({
  className: 'custom-icon',
  html: '<div style="background-color: blue; width: 20px; height: 20px; border-radius: 50%; border: 2px solid white;"></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const pendingIcon = L.divIcon({
  className: 'custom-icon',
  html: '<div style="background-color: yellow; width: 20px; height: 20px; border-radius: 50%; border: 2px solid white;"></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const Dashboard = () => {
  const [filters, setFilters] = useState({ counties: [], subCounties: [], selectedCounty: '', selectedSubCounty: '' });
  const [totalCases, setTotalCases] = useState(0);
  const [caseClassification, setCaseClassification] = useState([]);
  const [epiCurve, setEpiCurve] = useState([]);
  const [geoDistribution, setGeoDistribution] = useState([]);
  const [symptomPrevalence, setSymptomPrevalence] = useState([]);
  const [demographics, setDemographics] = useState([]);
  const [facilityLoad, setFacilityLoad] = useState([]);
  const [ageDistribution, setAgeDistribution] = useState([]);
  const [outcomeTrends, setOutcomeTrends] = useState([]);
  const [onsetToReportDelay, setOnsetToReportDelay] = useState([]);
  const [testResultsDistribution, setTestResultsDistribution] = useState({ combinations: [], flagged: [] });
  const [pathogenTestStatus, setPathogenTestStatus] = useState({ positive: 0, negative: 0, pending: 0 });
  const [elisaInsights, setElisaInsights] = useState({
    test_result_distribution: { ns1: {}, igm: {}, igg: {} },
    result_combinations: [],
    geo_distribution: [],
    temporal_trends: [],
    flagged_inconsistencies: [],
  });
  const [unspecifiedPositiveSamples, setUnspecifiedPositiveSamples] = useState([]);
  const [showFacilityBreakdown, setShowFacilityBreakdown] = useState(false);
  const [showElisaFacilityBreakdown, setShowElisaFacilityBreakdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showKemriTable, setShowKemriTable] = useState(false);
  const [kemriData, setKemriData] = useState([]);
  const [fieldSampleIds, setFieldSampleIds] = useState([]);
  const [selectedSampleNo, setSelectedSampleNo] = useState(null);
  const navigate = useNavigate();

  const API_BASE_URL = 'https://home.episurveillancekenya.com/api';

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }

    fetch(`${API_BASE_URL}/verify-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      mode: 'cors',
      credentials: 'include',
    })
      .then((res) => {
        if (!res.ok) throw new Error('Invalid or expired token');
        return res.json();
      })
      .then(() => {
        fetchFilterOptions();
      })
      .catch((err) => {
        localStorage.removeItem('token');
        navigate('/login', { replace: true });
      });
  }, [navigate]);

  const fetchFilterOptions = () => {
    fetch(`${API_BASE_URL}/`)
      .then((res) => res.json())
      .then((data) => {
        setFilters({
          counties: data.counties || [],
          subCounties: data.sub_counties || [],
          selectedCounty: '',
          selectedSubCounty: '',
        });
        fetchDashboardData();
      })
      .catch((err) => setError('Failed to load filters: ' + err.message))
      .finally(() => setLoading(false));
  };

  const fetchDashboardData = (county = '', subCounty = '') => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (county) params.append('county', county);
    if (subCounty) params.append('sub_county', subCounty);

    const endpoints = [
      { url: `/total_cases?${params}`, setter: setTotalCases, key: 'total_cases' },
      { url: `/case_classification?${params}`, setter: setCaseClassification },
      { url: `/epi_curve?${params}`, setter: setEpiCurve },
      { url: `/geo_distribution?${params}`, setter: setGeoDistribution },
      { url: `/symptom_prevalence`, setter: setSymptomPrevalence },
      { url: `/demographics`, setter: setDemographics },
      { url: `/facility_load`, setter: setFacilityLoad },
      { url: `/age_distribution?${params}`, setter: setAgeDistribution },
      { url: `/outcome_trends`, setter: setOutcomeTrends },
      { url: `/onset_to_report_delay`, setter: setOnsetToReportDelay },
      { url: `/test_results_distribution?${params}`, setter: setTestResultsDistribution },
      { url: `/pathogen_test_status?${params}`, setter: setPathogenTestStatus },
      { url: `/elisa_insights?${params}`, setter: setElisaInsights },
      { url: `/unspecified_positive_samples?${params}`, setter: setUnspecifiedPositiveSamples },
    ];

    endpoints.forEach(({ url, setter, key }) => {
      fetch(`${API_BASE_URL}${url}`, {
        headers: { Authorization: `Bearer ${token}` },
        mode: 'cors',
        credentials: 'include',
      })
        .then((res) => {
          if (!res.ok) throw new Error(`Failed to fetch ${url}`);
          return res.json();
        })
        .then((data) => setter(key ? data[key] : data))
        .catch((err) => setError(`Failed to load ${url}: ${err.message}`));
    });
    setLoading(false);
  };

  const fetchKemriData = () => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}/kemri_elisa_results`, {
      headers: { Authorization: `Bearer ${token}` },
      mode: 'cors',
      credentials: 'include',
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch KEMRI data');
        return res.json();
      })
      .then((data) => {
        setKemriData(data);
        setShowKemriTable(true);
      })
      .catch((err) => setError('Failed to load KEMRI data: ' + err.message));
  };

  const fetchFieldSampleIds = () => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}/samples/field_sample_ids`, {
      headers: { Authorization: `Bearer ${token}` },
      mode: 'cors',
      credentials: 'include',
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch Field Sample IDs');
        return res.json();
      })
      .then((data) => setFieldSampleIds(data))
      .catch((err) => setError('Failed to load Field Sample IDs: ' + err.message));
  };

  const handleLink = (sampleNo) => {
    setSelectedSampleNo(sampleNo);
    fetchFieldSampleIds();
  };

  const handleLinkSubmit = (fieldSampleId) => {
    if (!fieldSampleId) {
      setError('Please select a Field Sample ID');
      return;
    }
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}/samples/${fieldSampleId}/comment`, {
      headers: { Authorization: `Bearer ${token}` },
      mode: 'cors',
      credentials: 'include',
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch comment');
        return res.json();
      })
      .then((data) => {
        const { cleanedRdtResult } = data;
        fetch(`${API_BASE_URL}/kemri_elisa_results/update_rdt`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          mode: 'cors',
          credentials: 'include',
          body: JSON.stringify({ sampleNo: selectedSampleNo, rdtResult: cleanedRdtResult }),
        })
          .then((res) => {
            if (!res.ok) throw new Error('Failed to update RDT Results');
            return res.json();
          })
          .then(() => {
            fetchKemriData();
            setSelectedSampleNo(null);
            setFieldSampleIds([]);
          })
          .catch((err) => setError('Failed to update RDT Results: ' + err.message));
      })
      .catch((err) => setError('Failed to fetch comment: ' + err.message));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const newFilters = { ...prev, [name]: value };
      if (name === 'selectedCounty') newFilters.selectedSubCounty = '';
      fetchDashboardData(newFilters.selectedCounty, newFilters.selectedSubCounty);
      return newFilters;
    });
  };

  const toggleFacilityBreakdown = () => setShowFacilityBreakdown(!showFacilityBreakdown);
  const toggleElisaFacilityBreakdown = () => setShowElisaFacilityBreakdown(!showElisaFacilityBreakdown);

  const exportUnspecifiedPositiveToExcel = () => {
    const data = unspecifiedPositiveSamples.map(sample => ({
      'Sample ID': sample.id,
      'Field Sample ID': sample.fieldsampleid || 'N/A',
      'Comment': sample.comment || 'Blank',
      'Pathogen Test Result': sample.pathogentestresult,
      'Facility': sample.facility,
      'Participant Name': sample.participant_name || 'N/A',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Unspecified Positive Samples');
    XLSX.writeFile(workbook, 'unspecified_positive_samples.xlsx');
  };

  const exportRDTResultsToExcel = () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    if (filters.selectedCounty) params.append('county', filters.selectedCounty);
    if (filters.selectedSubCounty) params.append('sub_county', filters.selectedSubCounty);

    fetch(`${API_BASE_URL}/rdt_results?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
      mode: 'cors',
      credentials: 'include',
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch RDT results');
        return res.json();
      })
      .then((data) => {
        const excelData = data.map((item) => ({
          'Facility Name': item.facility_name || 'N/A',
          'Epid Number': item.epid_number || 'N/A',
          'RDT Results': item.rdt_results || 'Blank',
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'RDT Results');
        XLSX.writeFile(workbook, 'rdt_results.xlsx');
      })
      .catch((err) => setError('Failed to export RDT results: ' + err.message))
      .finally(() => setLoading(false));
  };

  const MapUpdater = ({ data, selectedCounty, selectedSubCounty, isElisa }) => {
    const map = useMap();
    useEffect(() => {
      if (data.length > 0) {
        const filteredData = selectedSubCounty
          ? data.filter((d) => {
              const facilitySubCounty = {
                'Bamba Sub-District Hospital': 'Ganze',
                'Chepchoina Dispensary': 'Cherangany',
                'Junda Dispensary': 'Kisauni',
                'Lukhome Dispensary': 'Saboti',
                'Mariakani District Hospital': 'Kaloleni',
                'Mvita Dispensary': 'Mvita',
                'Port Reitz District Hospital': 'Changamwe',
                'St Lukes (ACK) Hospital Kaloleni': 'Kaloleni',
              }[d.facility];
              return facilitySubCounty === selectedSubCounty;
            })
          : selectedCounty
          ? data.filter((d) => {
              const facilityCounty = {
                'Bamba Sub-District Hospital': 'Kilifi',
                'Chepchoina Dispensary': 'Trans Nzoia',
                'Junda Dispensary': 'Mombasa',
                'Lukhome Dispensary': 'Trans Nzoia',
                'Mariakani District Hospital': 'Kilifi',
                'Mvita Dispensary': 'Mombasa',
                'Port Reitz District Hospital': 'Mombasa',
                'St Lukes (ACK) Hospital Kaloleni': 'Kilifi',
              }[d.facility];
              return facilityCounty === selectedCounty;
            })
          : data;
        if (filteredData.length > 0) {
          const bounds = filteredData
            .filter((d) => d.lat && d.lon)
            .map((d) => [d.lat, d.lon]);
          if (bounds.length > 0) {
            map.fitBounds(bounds);
          }
        }
        map.setView([-1.2921, 36.8219], 6);
      }
    }, [map, data, selectedCounty, selectedSubCounty]);
    return null;
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Total Cases', totalCases],
      ...caseClassification.map((d) => ['Classification', d.caseclassification, d.count]),
      ...epiCurve.map((d) => ['Week', d.time, d.case_count]),
      ...geoDistribution.map((d) => ['Facility', d.facility, 'Positive Cases', d.positive, 'Negative Cases', d.negative, 'Pending Cases', d.pending]),
      ...symptomPrevalence.map((d) => ['Fever', d.fever, 'Headache', d.headache, 'Muscle Pain', d.musclepain, 'Joint Pain', d.jointpain, 'Nausea', d.nausea, 'Count', d.count]),
      ...demographics.map((d) => ['Sex', d.sex, 'Age', d.caseage, 'Sub-County', d.sub_county, 'Count', d.count]),
      ...facilityLoad.map((d) => ['Facility', d.facility, 'Cases', d.case_count]),
      ...ageDistribution.map((d) => ['Age Group', d.age_group, 'Count', d.count]),
      ...outcomeTrends.map((d) => ['Month', d.month, 'Outcome', d.outcome, 'Count', d.count]),
      ...onsetToReportDelay.map((d) => ['County', d.county, 'Avg Days to Report', d.avg_days]),
      ['Pathogen Test Status'],
      ['Positive', pathogenTestStatus.positive],
      ['Negative', pathogenTestStatus.negative],
      ['Pending', pathogenTestStatus.pending],
      ['Test Results Distribution'],
      ...testResultsDistribution.combinations.map((d) => [d.combination, d.count, ...d.facilities.map(f => `${f.name}: ${f.count}`).join('; ')]),
      ['Flagged Inconsistencies'],
      ...testResultsDistribution.flagged.map((f) => [f.fieldsampleid || 'N/A', f.id, f.comment || 'Blank', f.pathogentestresult, f.issue]),
      ['ELISA Test Result Distribution'],
      ['NS1 Positive', elisaInsights.test_result_distribution.ns1.positive],
      ['NS1 Negative', elisaInsights.test_result_distribution.ns1.negative],
      ['NS1 Equivocal', elisaInsights.test_result_distribution.ns1.equivocal],
      ['IgM Positive', elisaInsights.test_result_distribution.igm.positive],
      ['IgM Negative', elisaInsights.test_result_distribution.igm.negative],
      ['IgM Equivocal', elisaInsights.test_result_distribution.igm.equivocal],
      ['IgG Positive', elisaInsights.test_result_distribution.igg.positive],
      ['IgG Negative', elisaInsights.test_result_distribution.igg.negative],
      ['IgG Equivocal', elisaInsights.test_result_distribution.igg.equivocal],
      ['ELISA Result Combinations'],
      ...elisaInsights.result_combinations.map((d) => [d.combination, d.count, ...d.facilities.map(f => `${f.name}: ${f.count}`).join('; ')]),
      ['ELISA Flagged Inconsistencies'],
      ...elisaInsights.flagged_inconsistencies.map((f) => [f.sample_no, f.sample_id, f.ns1_results, f.igm_results, f.igg_results, f.rdt_results, f.issue]),
    ].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dengue_dashboard.csv';
    a.click();
  };

  const epiCurveChart = {
    labels: epiCurve.map((d) => d.time),
    datasets: [{
      label: 'Dengue Cases by Week',
      data: epiCurve.map((d) => d.case_count),
      borderColor: '#10B981',
      backgroundColor: 'rgba(16, 185, 129, 0.2)',
      fill: true,
    }],
  };

  const caseClassificationChart = {
    labels: caseClassification.map((d) => d.caseclassification),
    datasets: [{
      label: 'Case Classification',
      data: caseClassification.map((d) => d.count),
      backgroundColor: ['#EF4444', '#3B82F6', '#FBBF24', '#10B981'],
    }],
  };

  const symptomChart = {
    labels: ['Fever', 'Headache', 'Muscle Pain', 'Joint Pain', 'Nausea'],
    datasets: [{
      label: 'Symptom Prevalence',
      data: symptomPrevalence.reduce(
        (acc, curr) => [
          acc[0] + (curr.fever ? curr.count : 0),
          acc[1] + (curr.headache ? curr.count : 0),
          acc[2] + (curr.musclepain ? curr.count : 0),
          acc[3] + (curr.jointpain ? curr.count : 0),
          acc[4] + (curr.nausea ? curr.count : 0),
        ],
        [0, 0, 0, 0, 0]
      ),
      backgroundColor: '#3B82F6',
    }],
  };

  const demographicsChart = {
    labels: [...new Set(demographics.map((d) => d.sub_county))],
    datasets: [
      {
        label: 'Male',
        data: demographics.filter((d) => d.sex === 'MALE').map((d) => d.count),
        backgroundColor: '#3B82F6',
      },
      {
        label: 'Female',
        data: demographics.filter((d) => d.sex === 'FEMALE').map((d) => d.count),
        backgroundColor: '#EF4444',
      },
    ],
  };

  const facilityLoadChart = {
    labels: facilityLoad.map((d) => d.facility),
    datasets: [{
      label: 'Cases per Facility',
      data: facilityLoad.map((d) => d.case_count),
      backgroundColor: 'rgba(16, 185, 129, 0.2)',
      borderColor: '#10B981',
      borderWidth: 1,
    }],
  };

  const ageDistributionChart = {
    labels: ageDistribution.map((d) => d.age_group),
    datasets: [{
      label: 'Age Distribution',
      data: ageDistribution.map((d) => d.count),
      backgroundColor: '#10B981',
    }],
  };

  const outcomeTrendsChart = {
    labels: [...new Set(outcomeTrends.map((d) => d.month))],
    datasets: [...new Set(outcomeTrends.map((d) => d.outcome))].map((outcome, index) => ({
      label: outcome,
      data: outcomeTrends.filter((d) => d.outcome === outcome).map((d) => d.count),
      borderColor: ['#EF4444', '#10B981', '#3B82F6'][index % 3],
      fill: false,
    })),
  };

  const onsetToReportDelayChart = {
    labels: onsetToReportDelay.map((d) => d.county),
    datasets: [{
      label: 'Avg Days from Onset to Report',
      data: onsetToReportDelay.map((d) => d.avg_days),
      backgroundColor: '#8B5CF6',
    }],
  };

  const testResultsChart = showFacilityBreakdown
    ? {
        labels: [...new Set(testResultsDistribution.combinations.flatMap(d => d.facilities.map(f => f.name)))],
        datasets: testResultsDistribution.combinations.map((d, index) => ({
          label: d.combination,
          data: [...new Set(testResultsDistribution.combinations.flatMap(d => d.facilities.map(f => f.name)))].map(facility => {
            const facilityData = d.facilities.find(f => f.name === facility);
            return facilityData ? facilityData.count : 0;
          }),
          backgroundColor: ['#EF4444', '#3B82F6', '#FBBF24', '#10B981', '#8B5CF6', '#F472B6', '#4B5563', '#6EE7B7', '#F97316'][index % 9],
        })),
      }
    : {
        labels: testResultsDistribution.combinations.map(d => d.combination),
        datasets: [{
          label: 'Sample Count',
          data: testResultsDistribution.combinations.map(d => d.count),
          backgroundColor: ['#EF4444', '#3B82F6', '#FBBF24', '#10B981', '#8B5CF6', '#F472B6', '#4B5563', '#6EE7B7', '#F97316'],
        }],
      };

  const pathogenTestStatusChart = {
    labels: ['Pathogen Test Status'],
    datasets: [
      {
        label: 'Positive',
        data: [pathogenTestStatus.positive],
        backgroundColor: '#EF4444',
      },
      {
        label: 'Negative',
        data: [pathogenTestStatus.negative],
        backgroundColor: '#3B82F6',
      },
      {
        label: 'Pending',
        data: [pathogenTestStatus.pending],
        backgroundColor: '#FBBF24',
      },
    ],
  };

  const elisaTestResultChart = {
    labels: ['NS1', 'IgM', 'IgG'],
    datasets: [
      {
        label: 'Positive',
        data: [
          elisaInsights.test_result_distribution.ns1.positive,
          elisaInsights.test_result_distribution.igm.positive,
          elisaInsights.test_result_distribution.igg.positive,
        ],
        backgroundColor: '#EF4444',
      },
      {
        label: 'Negative',
        data: [
          elisaInsights.test_result_distribution.ns1.negative,
          elisaInsights.test_result_distribution.igm.negative,
          elisaInsights.test_result_distribution.igg.negative,
        ],
        backgroundColor: '#3B82F6',
      },
      {
        label: 'Equivocal',
        data: [
          elisaInsights.test_result_distribution.ns1.equivocal,
          elisaInsights.test_result_distribution.igm.equivocal,
          elisaInsights.test_result_distribution.igg.equivocal,
        ],
        backgroundColor: '#FBBF24',
      },
    ],
  };

  const elisaCombinationsChart = showElisaFacilityBreakdown
    ? {
        labels: [...new Set(elisaInsights.result_combinations.flatMap(d => d.facilities.map(f => f.name)))],
        datasets: elisaInsights.result_combinations.map((d, index) => ({
          label: d.combination,
          data: [...new Set(elisaInsights.result_combinations.flatMap(d => d.facilities.map(f => f.name)))].map(facility => {
            const facilityData = d.facilities.find(f => f.name === facility);
            return facilityData ? facilityData.count : 0;
          }),
          backgroundColor: ['#EF4444', '#3B82F6', '#FBBF24', '#10B981', '#8B5CF6', '#F472B6', '#4B5563', '#6EE7B7', '#F97316'][index % 9],
        })),
      }
    : {
        labels: elisaInsights.result_combinations.map(d => d.combination),
        datasets: [{
          label: 'Sample Count',
          data: elisaInsights.result_combinations.map(d => d.count),
          backgroundColor: ['#EF4444', '#3B82F6', '#FBBF24', '#10B981', '#8B5CF6', '#F472B6', '#4B5563', '#6EE7B7', '#F97316'],
        }],
      };

  const elisaTemporalTrendsChart = {
    labels: elisaInsights.temporal_trends.map(d => d.group),
    datasets: [
      {
        label: 'Positive NS1',
        data: elisaInsights.temporal_trends.map(d => d.positive_ns1),
        borderColor: '#EF4444',
        fill: false,
      },
      {
        label: 'Positive IgM',
        data: elisaInsights.temporal_trends.map(d => d.positive_igm),
        borderColor: '#3B82F6',
        fill: false,
      },
      {
        label: 'Positive IgG',
        data: elisaInsights.temporal_trends.map(d => d.positive_igg),
        borderColor: '#FBBF24',
        fill: false,
      },
    ],
  };

  return (
    <div className="container mx-auto p-4 bg-gray-50 min-h-screen font-poppins">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-blue-800">Dengue Project Dashboard</h2>
        <div className="flex space-x-4">
          <button onClick={fetchKemriData} className="px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600 transition-all">
            Link Kemri Lab
          </button>
          <button
            onClick={() => {
              localStorage.removeItem('token');
              navigate('/login', { replace: true });
            }}
            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-all"
          >
            Logout
          </button>
        </div>
      </div>
      {error && <p className="text-red-500 mb-4 text-center">{error}</p>}
      {loading ? (
        <p className="text-gray-600 text-center">Loading...</p>
      ) : (
        <div>
          <div className="mb-8 flex flex-wrap gap-4">
            <select
              name="selectedCounty"
              value={filters.selectedCounty}
              onChange={handleFilterChange}
              className="p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 flex-1 min-w-[150px]"
            >
              <option value="">All Counties</option>
              {filters.counties.map((county, index) => (
                <option key={index} value={county}>{county}</option>
              ))}
            </select>
            <select
              name="selectedSubCounty"
              value={filters.selectedSubCounty}
              onChange={handleFilterChange}
              className="p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 flex-1 min-w-[150px]"
              disabled={!filters.selectedCounty}
            >
              <option value="">All Sub-Counties</option>
              {filters.subCounties.map((subCounty, index) => (
                <option key={index} value={subCounty}>{subCounty}</option>
              ))}
            </select>
            <button onClick={() => setFilters({ ...filters, selectedCounty: '', selectedSubCounty: '' })} className="px-4 py-3 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-all">
              Reset Filters
            </button>
            <button onClick={exportToCSV} className="px-4 py-3 bg-green-500 text-white rounded-md hover:bg-green-600 transition-all">
              Export to CSV
            </button>
            <button onClick={exportUnspecifiedPositiveToExcel} className="px-4 py-3 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-all">
              Download Unspecified Positives (Excel)
            </button>
            <button onClick={exportRDTResultsToExcel} className="px-4 py-3 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 transition-all">
              Download RDT Results (Excel)
            </button>
          </div>

          {showKemriTable && (
            <div className="p-6 bg-white rounded-xl shadow-lg mb-8 overflow-x-auto">
              <h3 className="text-2xl font-semibold text-blue-800 mb-4">KEMRI ELISA Results</h3>
              <table className="min-w-full bg-white border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="py-3 px-4 border-b text-left text-gray-700">Sample No</th>
                    <th className="py-3 px-4 border-b text-left text-gray-700">Sample ID</th>
                    <th className="py-3 px-4 border-b text-left text-gray-700">NS1 Results</th>
                    <th className="py-3 px-4 border-b text-left text-gray-700">IgM Results</th>
                    <th className="py-3 px-4 border-b text-left text-gray-700">IgG Results</th>
                    <th className="py-3 px-4 border-b text-left text-gray-700">RDT Results</th>
                    <th className="py-3 px-4 border-b text-left text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {kemriData.map((row) => (
                    <tr key={row.sample_no} className="hover:bg-gray-50">
                      <td className="py-3 px-4 border-b">{row.sample_no}</td>
                      <td className="py-3 px-4 border-b">{row.sample_id}</td>
                      <td className="py-3 px-4 border-b">{row.ns1_results}</td>
                      <td className="py-3 px-4 border-b">{row.igm_results}</td>
                      <td className="py-3 px-4 border-b">{row.igg_results}</td>
                      <td className="py-3 px-4 border-b">{row.rdt_results || 'Not Linked'}</td>
                      <td className="py-3 px-4 border-b">
                        <button onClick={() => handleLink(row.sample_no)} className="px-3 py-1 bg-teal-500 text-white rounded-md hover:bg-teal-600 transition-all">
                          Link
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {selectedSampleNo && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
                <h3 className="text-2xl font-semibold text-blue-800 mb-4">Link Sample {selectedSampleNo}</h3>
                <Select
                  options={fieldSampleIds.map((id) => ({ value: id, label: id }))}
                  onChange={(option) => handleLinkSubmit(option?.value)}
                  placeholder="Select Field Sample ID"
                  isSearchable
                  className="mb-4"
                />
                <div className="flex justify-end space-x-4">
                  <button onClick={() => setSelectedSampleNo(null)} className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-all">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="p-6 bg-white rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold text-blue-800">Total Cases</h3>
              <p className="text-4xl font-bold text-teal-600">{totalCases}</p>
            </div>
            <div className="p-6 bg-white rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold text-blue-800">Case Classification</h3>
              <Pie data={caseClassificationChart} options={{ plugins: { legend: { position: 'bottom' } } }} />
            </div>
            <div className="p-6 bg-white rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold text-blue-800">Pathogen Test Status</h3>
              <Bar data={pathogenTestStatusChart} options={{ plugins: { legend: { position: 'top' } }, scales: { x: { stacked: true }, y: { stacked: true } } }} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="p-6 bg-white rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold text-blue-800 mb-4">Epidemiological Curve</h3>
              <Line data={epiCurveChart} options={{ plugins: { legend: { position: 'top' } } }} />
            </div>
            <div className="p-6 bg-white rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold text-blue-800 mb-4">Geographic Distribution</h3>
              <MapContainer center={[-1.2921, 36.8219]} zoom={6} style={{ height: '400px', borderRadius: '12px' }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='© OpenStreetMap' />
                <MapUpdater data={geoDistribution} selectedCounty={filters.selectedCounty} selectedSubCounty={filters.selectedSubCounty} isElisa={false} />
                {geoDistribution.map((point, index) => (
                  point.lat && point.lon && (
                    <React.Fragment key={index}>
                      {point.positive > 0 && <Marker position={[point.lat, point.lon]} icon={positiveIcon}><Popup>{/* Popup content */}</Popup></Marker>}
                      {point.negative > 0 && <Marker position={[point.lat + 0.001, point.lon + 0.001]} icon={negativeIcon}><Popup>{/* Popup content */}</Popup></Marker>}
                      {point.pending > 0 && <Marker position={[point.lat - 0.001, point.lon - 0.001]} icon={pendingIcon}><Popup>{/* Popup content */}</Popup></Marker>}
                    </React.Fragment>
                  )
                ))}
              </MapContainer>
            </div>
            <div className="p-6 bg-white rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold text-blue-800 mb-4">Symptom Prevalence</h3>
              <Bar data={symptomChart} options={{ plugins: { legend: { position: 'top' } } }} />
            </div>
            <div className="p-6 bg-white rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold text-blue-800 mb-4">
                Test Results Distribution
                <button onClick={toggleFacilityBreakdown} className="ml-4 px-3 py-1 bg-teal-500 text-white rounded-md hover:bg-teal-600 transition-all">
                  {showFacilityBreakdown ? 'Show Total' : 'Show by Facility'}
                </button>
              </h3>
              <Bar data={testResultsChart} options={{ plugins: { legend: { position: 'top' } }, scales: { x: { stacked: false }, y: { stacked: false } } }} />
            </div>
            <div className="p-6 bg-white rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold text-blue-800 mb-4">Demographics</h3>
              <Bar data={demographicsChart} options={{ indexAxis: 'y', plugins: { legend: { position: 'top' } } }} />
            </div>
            <div className="p-6 bg-white rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold text-blue-800 mb-4">Facility Load</h3>
              <Bar data={facilityLoadChart} options={{ indexAxis: 'y', plugins: { legend: { position: 'top' } } }} />
            </div>
            <div className="p-6 bg-white rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold text-blue-800 mb-4">Age Distribution</h3>
              <Bar data={ageDistributionChart} options={{ plugins: { legend: { position: 'top' } } }} />
            </div>
            <div className="p-6 bg-white rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold text-blue-800 mb-4">Outcome Trends</h3>
              <Line data={outcomeTrendsChart} options={{ plugins: { legend: { position: 'top' } } }} />
            </div>
            <div className="p-6 bg-white rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold text-blue-800 mb-4">Symptom Onset to Reporting Delay</h3>
              <Bar data={onsetToReportDelayChart} options={{ plugins: { legend: { position: 'top' } } }} />
            </div>
            <div className="p-6 bg-white rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold text-blue-800 mb-4">ELISA Test Result Distribution</h3>
              <Bar data={elisaTestResultChart} options={{ plugins: { legend: { position: 'top' } }, scales: { x: { stacked: true }, y: { stacked: true } } }} />
            </div>
            <div className="p-6 bg-white rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold text-blue-800 mb-4">
                ELISA Result Combinations
                <button onClick={toggleElisaFacilityBreakdown} className="ml-4 px-3 py-1 bg-teal-500 text-white rounded-md hover:bg-teal-600 transition-all">
                  {showElisaFacilityBreakdown ? 'Show Total' : 'Show by Facility'}
                </button>
              </h3>
              <Bar data={elisaCombinationsChart} options={{ plugins: { legend: { position: 'top' } }, scales: { x: { stacked: false }, y: { stacked: false } } }} />
            </div>
            <div className="p-6 bg-white rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold text-blue-800 mb-4">ELISA Geographic Distribution</h3>
              <MapContainer center={[-1.2921, 36.8219]} zoom={6} style={{ height: '400px', borderRadius: '12px' }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='© OpenStreetMap' />
                <MapUpdater data={elisaInsights.geo_distribution} selectedCounty={filters.selectedCounty} selectedSubCounty={filters.selectedSubCounty} isElisa={true} />
                {elisaInsights.geo_distribution.map((point, index) => (
                  point.lat && point.lon && (
                    <React.Fragment key={index}>
                      {point.positive.ns1 > 0 && <Marker position={[point.lat, point.lon]} icon={positiveIcon}><Popup>{/* Popup content */}</Popup></Marker>}
                      {point.negative.ns1 > 0 && <Marker position={[point.lat + 0.001, point.lon + 0.001]} icon={negativeIcon}><Popup>{/* Popup content */}</Popup></Marker>}
                      {point.equivocal.ns1 > 0 && <Marker position={[point.lat - 0.001, point.lon - 0.001]} icon={pendingIcon}><Popup>{/* Popup content */}</Popup></Marker>}
                    </React.Fragment>
                  )
                ))}
              </MapContainer>
            </div>
            <div className="p-6 bg-white rounded-xl shadow-lg">
              <h3 className="text-lg font-semibold text-blue-800 mb-4">ELISA Temporal Trends</h3>
              <Line data={elisaTemporalTrendsChart} options={{ plugins: { legend: { position: 'top' } } }} />
            </div>
            {elisaInsights.flagged_inconsistencies.length > 0 && (
              <div className="p-6 bg-white rounded-xl shadow-lg">
                <h3 className="text-lg font-semibold text-blue-800 mb-4">ELISA Flagged Inconsistencies</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="py-3 px-4 border-b text-left text-gray-700">Sample No</th>
                        <th className="py-3 px-4 border-b text-left text-gray-700">Sample ID</th>
                        <th className="py-3 px-4 border-b text-left text-gray-700">NS1 Result</th>
                        <th className="py-3 px-4 border-b text-left text-gray-700">IgM Result</th>
                        <th className="py-3 px-4 border-b text-left text-gray-700">IgG Result</th>
                        <th className="py-3 px-4 border-b text-left text-gray-700">RDT Result</th>
                        <th className="py-3 px-4 border-b text-left text-gray-700">Issue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {elisaInsights.flagged_inconsistencies.map((f, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="py-3 px-4 border-b">{f.sample_no}</td>
                          <td className="py-3 px-4 border-b">{f.sample_id}</td>
                          <td className="py-3 px-4 border-b">{f.ns1_results}</td>
                          <td className="py-3 px-4 border-b">{f.igm_results}</td>
                          <td className="py-3 px-4 border-b">{f.igg_results}</td>
                          <td className="py-3 px-4 border-b">{f.rdt_results || 'N/A'}</td>
                          <td className="py-3 px-4 border-b">{f.issue}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {testResultsDistribution.flagged.length > 0 && (
              <div className="p-6 bg-white rounded-xl shadow-lg">
                <h3 className="text-lg font-semibold text-blue-800 mb-4">Flagged Inconsistent Samples</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="py-3 px-4 border-b text-left text-gray-700">Field Sample ID</th>
                        <th className="py-3 px-4 border-b text-left text-gray-700">Sample ID</th>
                        <th className="py-3 px-4 border-b text-left text-gray-700">Comment</th>
                        <th className="py-3 px-4 border-b text-left text-gray-700">Pathogen Test Result</th>
                        <th className="py-3 px-4 border-b text-left text-gray-700">Issue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testResultsDistribution.flagged.map((f, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="py-3 px-4 border-b">{f.fieldsampleid || 'N/A'}</td>
                          <td className="py-3 px-4 border-b">{f.id}</td>
                          <td className="py-3 px-4 border-b">{f.comment || 'Blank'}</td>
                          <td className="py-3 px-4 border-b">{f.pathogentestresult}</td>
                          <td className="py-3 px-4 border-b">{f.issue}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {unspecifiedPositiveSamples.length > 0 && (
              <div className="p-6 bg-white rounded-xl shadow-lg">
                <h3 className="text-lg font-semibold text-blue-800 mb-4">Unspecified Positive Samples</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="py-3 px-4 border-b text-left text-gray-700">Sample ID</th>
                        <th className="py-3 px-4 border-b text-left text-gray-700">Field Sample ID</th>
                        <th className="py-3 px-4 border-b text-left text-gray-700">Comment</th>
                        <th className="py-3 px-4 border-b text-left text-gray-700">Pathogen Test Result</th>
                        <th className="py-3 px-4 border-b text-left text-gray-700">Facility</th>
                        <th className="py-3 px-4 border-b text-left text-gray-700">Participant Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unspecifiedPositiveSamples.map((sample, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="py-3 px-4 border-b">{sample.id}</td>
                          <td className="py-3 px-4 border-b">{sample.fieldsampleid || 'N/A'}</td>
                          <td className="py-3 px-4 border-b">{sample.comment || 'Blank'}</td>
                          <td className="py-3 px-4 border-b">{sample.pathogentestresult}</td>
                          <td className="py-3 px-4 border-b">{sample.facility}</td>
                          <td className="py-3 px-4 border-b">{sample.participant_name || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 p-6 bg-white rounded-xl shadow-lg">
            <h3 className="text-lg font-semibold text-blue-800 mb-4">Other Projects</h3>
            <ul className="list-disc pl-5 text-teal-500 space-y-2">
              <li><a href="https://example.com/malaria" target="_blank" className="hover:underline">Malaria Surveillance Platform</a> - AI predictions for outbreaks.</li>
              <li><a href="https://example.com/tb" target="_blank" className="hover:underline">TB Digital Management Tool</a> - Tracking and reporting system.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;